import React, { useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { TRADESTATION_AOP_URL } from '../../constants/tradestationReferral';
import analyticsService from '../../services/analyticsService';
import {
  getConnectTradeBrokersForPlanTable,
  getBrokerAssetClasses,
  getBrokerPlanAvailability,
} from '../../data/shared/connectTradeBrokers';
import LandingBrokerWaitlistForm from '../landing/LandingBrokerWaitlistForm';
import {
  TradeStationBrokerSubcaption,
  TradeStationReferralPromoInSupportedBrokersSection,
} from '../shared/TradeStationReferralPromoBar';
import { MarketingHeadlineAccent } from '../shared/MarketingHeadlineAccent';
import '../shared/TradeStationReferralPromoBar.css';
import './PricingBrokerSupportTable.css';

function PlanBadge({ availability }) {
  if (availability.kind === 'unsupported') {
    return <span className="pricing-broker-plan-unsupported">{availability.label}</span>;
  }

  return (
    <span className="pricing-broker-plan">
      <span className={`pricing-broker-plan-badge pricing-broker-plan-badge--${availability.kind}`}>
        {availability.label}
      </span>
      {availability.kind === 'both' && (
        <span className="pricing-broker-plan-badge pricing-broker-plan-badge--track">Track</span>
      )}
    </span>
  );
}

function AssetClassesCell({ broker }) {
  const classes = getBrokerAssetClasses(broker);
  if (!classes.length) {
    return <span className="pricing-broker-asset-empty">—</span>;
  }

  return (
    <div className="pricing-broker-assets">
      {classes.map((label) => (
        <span
          key={label}
          className={`pricing-broker-asset-tag pricing-broker-asset-tag--${label.toLowerCase()}`}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function BrokerNameCell({ broker, showTradeStationPromo, comingSoon }) {
  const publicUrl = process.env.PUBLIC_URL || '';
  const fit = broker.logoFit === 'contain' ? 'contain' : 'cover';
  const wrapClass = [
    'pricing-broker-logo-wrap',
    fit === 'contain' ? 'pricing-broker-logo-wrap--contain' : '',
    broker.logoBg === 'dark' ? 'pricing-broker-logo-wrap--dark' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`pricing-broker-name${showTradeStationPromo ? ' pricing-broker-name--with-promo' : ''}`}>
      <div className="pricing-broker-name-row">
        <div className={wrapClass}>
          <img
            src={`${publicUrl}${broker.logoSrc}`}
            alt=""
            className="pricing-broker-logo"
            loading="lazy"
            decoding="async"
          />
        </div>
        <span className="pricing-broker-label">{broker.name}</span>
        {comingSoon ? (
          <span className="pricing-broker-coming-soon">{comingSoon}</span>
        ) : null}
      </div>
      {showTradeStationPromo && (
        <TradeStationBrokerSubcaption
          analyticsSource="pricing_supported_brokers_table"
          className="landing-ts-supported-promo pricing-broker-ts-promo"
        />
      )}
    </div>
  );
}

const DEFAULT_WAITLIST_NOTE =
  "We're hard at work on a multi-broker integration, which will unlock many of the brokers below marked Coming soon.";

export default function PricingBrokerSupportTable({
  connectTradeLaunched,
  landingPageId = 'pricing',
  embedded = false,
  title = 'Supported Brokers',
  titleAccent = 'Brokers',
  waitlistNote = DEFAULT_WAITLIST_NOTE,
  waitlistCopy = 'Get notified when we add support for your broker.',
}) {
  const { user } = useContext(AuthContext) || {};
  const brokers = getConnectTradeBrokersForPlanTable();
  const showTradeStationRowPromo = user?.hasTradeStationCredentials !== true;
  const SectionTag = embedded ? 'div' : 'section';

  return (
    <SectionTag
      id={embedded ? undefined : 'supported-brokers'}
      className={`pricing-brokers-section${embedded ? ' pricing-brokers-section--embedded' : ''}`}
      aria-labelledby="pricing-brokers-heading"
    >
      <div className="pricing-brokers-intro">
        <h2 id="pricing-brokers-heading" className="pricing-brokers-heading">
          <MarketingHeadlineAccent text={title} accent={titleAccent} />
        </h2>
        <div className="pricing-brokers-intro-copy">
          <TradeStationReferralPromoInSupportedBrokersSection />
          <p className="pricing-brokers-subtitle">
            <strong className="pricing-brokers-plan-name">Track</strong> is free and covers rule alerts, journaling, and analytics.
            {' '}
            <strong className="pricing-brokers-plan-name">Trade</strong> adds order entry, live charts, and rules that block order placement.
          </p>
          <p className="pricing-brokers-subtitle">
            For full Trade support, use our #1 recommended broker,{' '}
            <a
              href={TRADESTATION_AOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-brokers-promo-link"
              onClick={() =>
                analyticsService.trackTradingEvent('tradestation_aop_link_clicked', {
                  source: 'supported_brokers_intro',
                })
              }
            >
              TradeStation
            </a>
            . It only takes a few minutes to get started.
          </p>
          {waitlistNote && !connectTradeLaunched && (
            <p className="pricing-brokers-integration-note">{waitlistNote}</p>
          )}
        </div>
      </div>

      <div className="pricing-brokers-table-shell">
        <div className="pricing-brokers-table-header">
          <span className="pricing-compare-feature-label">Broker</span>
          <span className="pricing-compare-feature-label pricing-brokers-plan-header">Plan</span>
          <span className="pricing-compare-feature-label pricing-brokers-assets-header">
            <span className="pricing-brokers-col-hdr__long">Asset classes</span>
            <span className="pricing-brokers-col-hdr__short">Assets</span>
          </span>
        </div>

        {brokers.map((broker, i) => {
          const availability = getBrokerPlanAvailability(broker);
          return (
          <div
            key={broker.id}
            className="pricing-brokers-table-row"
            style={{
              borderBottom: i < brokers.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
            }}
          >
            <BrokerNameCell
              broker={broker}
              showTradeStationPromo={broker.id === 'tradestation' && showTradeStationRowPromo}
              comingSoon={availability.note}
            />
            <span className="pricing-brokers-tier-cell">
              <PlanBadge availability={availability} />
            </span>
            <AssetClassesCell broker={broker} />
          </div>
          );
        })}
      </div>

      <div className="pricing-brokers-waitlist">
        <LandingBrokerWaitlistForm
          landingPageId={landingPageId}
          waitlistCopy={waitlistCopy}
          buttonLabel="Get notified"
        />
      </div>
    </SectionTag>
  );
}
